import { z } from 'zod';

/** 분석 모드 코드 (BE ContentAnalysisProfile.code 와 동일). */
export const ContentAnalysisProfileSchema = z.union([
  z.literal(1), // SOCCER
  z.literal(3), // GENERAL_STT_LITE (일반)
  z.literal('SOCCER').transform(() => 1),
]);
export type ContentAnalysisProfile = z.infer<
  typeof ContentAnalysisProfileSchema
>;

/** 분석 모드 표시 라벨 (admin 모드 선택 UI). */
export const ContentAnalysisProfileLabel: Record<number, string> = {
  1: '축구',
  3: '일반',
};

export const BroadcastVaultSourceStatusSchema = z.union([
  z.literal(1).transform(() => 'ACTIVE' as const),
  z.literal(2).transform(() => 'DELETED' as const),
  z.literal('ACTIVE'),
  z.literal('DELETED'),
]);
export type BroadcastVaultSourceStatus = z.infer<
  typeof BroadcastVaultSourceStatusSchema
>;

const NullableStringSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) => value ?? '');

const NullableNumberSchema = z
  .number()
  .nullable()
  .optional()
  .transform((value) => value ?? 0);

export const AiProcessingStatusSchema = z.union([
  z.literal(-1).transform(() => 'ERROR' as const),
  z.literal(0).transform(() => 'NOT_READY' as const),
  z.literal(1).transform(() => 'COMPLETE' as const),
  z.literal(2).transform(() => 'START_PROCESSING' as const),
  z.literal('NOT_STARTED'),
  z.literal('NOT_READY'),
  z.literal('START_PROCESSING'),
  z.literal('PROCESSING'),
  z.literal('COMPLETE'),
  z.literal('COMPLETED'),
  z.literal('ERROR'),
  z.literal('UNKNOWN'),
  z.null().transform(() => 'UNKNOWN' as const),
  z.undefined().transform(() => 'UNKNOWN' as const),
]);
export type BroadcastVaultRawAnalysisStatus =
  | z.input<typeof AiProcessingStatusSchema>
  | string;
export type AiProcessingStatus = z.infer<typeof AiProcessingStatusSchema>;

const normalizeAnalysisStatus = (
  status: BroadcastVaultRawAnalysisStatus,
): AiProcessingStatus => {
  const parsed = AiProcessingStatusSchema.safeParse(status);
  return parsed.success ? parsed.data : 'UNKNOWN';
};

export const deriveBroadcastVaultAnalysisStatus = (
  rawIndexingStatus: BroadcastVaultRawAnalysisStatus,
  rawEmbeddingStatus: BroadcastVaultRawAnalysisStatus,
): BroadcastVaultDerivedStatus => {
  const indexingStatus = normalizeAnalysisStatus(rawIndexingStatus);
  const embeddingStatus = normalizeAnalysisStatus(rawEmbeddingStatus);

  if (indexingStatus === 'ERROR' || embeddingStatus === 'ERROR') {
    return 'ERROR' as const;
  }
  if (
    indexingStatus === 'START_PROCESSING' ||
    indexingStatus === 'PROCESSING' ||
    embeddingStatus === 'START_PROCESSING' ||
    embeddingStatus === 'PROCESSING'
  ) {
    return 'PROCESSING' as const;
  }
  if (
    (indexingStatus === 'COMPLETE' || indexingStatus === 'COMPLETED') &&
    (embeddingStatus === 'COMPLETE' || embeddingStatus === 'COMPLETED')
  ) {
    return 'READY' as const;
  }
  return 'NOT_ANALYZED' as const;
};

export const BroadcastVaultDerivedStatusSchema = z.enum([
  'READY',
  'PROCESSING',
  'ERROR',
  'NOT_ANALYZED',
]);
export type BroadcastVaultDerivedStatus = z.infer<
  typeof BroadcastVaultDerivedStatusSchema
>;
export type BroadcastVaultAnalysisStatus = BroadcastVaultDerivedStatus;

export const BroadcastVaultAnalysisStatusLabel: Record<
  BroadcastVaultDerivedStatus,
  string
> = {
  READY: '분석 완료',
  PROCESSING: '분석 중',
  ERROR: '분석 오류',
  NOT_ANALYZED: '분석 필요',
};

export const BroadcastVaultSourceSummarySchema = z.object({
  totalContentCount: z.number().default(0),
  readyContentCount: z.number().default(0),
  processingContentCount: z.number().default(0),
  errorContentCount: z.number().default(0),
  notAnalyzedContentCount: z.number().default(0),
});
export type BroadcastVaultSourceSummary = z.infer<
  typeof BroadcastVaultSourceSummarySchema
>;

export const BroadcastVaultSourceSchema = z.object({
  id: z.number(),
  sourceSpaceId: z.number(),
  sourceSpaceName: z.string().optional(),
  sourceFolderId: z.number(),
  sourceFolderName: NullableStringSchema,
  sourceFolderPath: NullableStringSchema,
  contentAnalysisProfile: ContentAnalysisProfileSchema,
  /** 이 source 의 tenant 에서 선택 가능한 분석 모드 목록 (구버전 BE 호환: 누락 시 []). */
  availableAnalysisProfiles: z
    .array(ContentAnalysisProfileSchema)
    .optional()
    .default([]),
  status: BroadcastVaultSourceStatusSchema,
  createdByAccountId: z.number().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  summary: BroadcastVaultSourceSummarySchema.optional(),
});
export type BroadcastVaultSource = z.infer<typeof BroadcastVaultSourceSchema>;

export const BroadcastVaultSourceCreateRequestSchema = z.object({
  sourceSpaceId: z.number(),
  sourceFolderId: z.number(),
  contentAnalysisProfile: ContentAnalysisProfileSchema,
});
export type BroadcastVaultSourceCreateRequest = z.infer<
  typeof BroadcastVaultSourceCreateRequestSchema
>;

export const BroadcastVaultSourceUpdateRequestSchema = z.object({
  /** 변경할 기본 분석 모드. 이미 분석된 콘텐츠 데이터에는 영향 없음 (재분석 필요). */
  contentAnalysisProfile: ContentAnalysisProfileSchema,
});
export type BroadcastVaultSourceUpdateRequest = z.infer<
  typeof BroadcastVaultSourceUpdateRequestSchema
>;

export const BroadcastVaultAvailableSourceSchema =
  BroadcastVaultSourceSchema.pick({
    id: true,
    sourceSpaceId: true,
    sourceSpaceName: true,
    sourceFolderId: true,
    sourceFolderName: true,
    sourceFolderPath: true,
    contentAnalysisProfile: true,
  });
export type BroadcastVaultAvailableSource = z.infer<
  typeof BroadcastVaultAvailableSourceSchema
>;

export const BroadcastVaultAnalysisTargetSchema = z
  .object({
    contentId: z.number(),
    folderId: z.number().optional(),
    folderPath: NullableStringSchema,
    name: z.string().optional(),
    contentName: z.string().optional(),
    thumbnailPublicUrl: NullableStringSchema,
    contentType: z.union([z.number(), z.string()]).optional(),
    indexingStatus: AiProcessingStatusSchema,
    embeddingStatus: AiProcessingStatusSchema,
    createdAt: NullableStringSchema,
  })
  .transform((target) => {
    const indexingStatus = target.indexingStatus;
    const embeddingStatus = target.embeddingStatus;
    return {
      ...target,
      contentName: target.contentName ?? target.name ?? '',
      indexingStatus,
      embeddingStatus,
      derivedStatus: deriveBroadcastVaultAnalysisStatus(
        indexingStatus,
        embeddingStatus,
      ),
    };
  });
export type BroadcastVaultAnalysisTarget = z.infer<
  typeof BroadcastVaultAnalysisTargetSchema
>;

export const BroadcastVaultAnalysisRequestSchema = z.discriminatedUnion(
  'mode',
  [
    z.object({
      mode: z.literal('SELECTED'),
      contentIds: z.array(z.number()).min(1),
      // true 면 이미 분석 완료(READY)된 영상도 강제 재분석. 미지정 시 BE 기본값 false.
      force: z.boolean().optional(),
      // 분석 모드. 미지정 시 BE 가 source 기본값 사용.
      analysisProfile: ContentAnalysisProfileSchema.optional(),
    }),
    z.object({
      mode: z.literal('ALL_UNANALYZED'),
      force: z.boolean().optional(),
      analysisProfile: ContentAnalysisProfileSchema.optional(),
    }),
  ],
);
export type BroadcastVaultAnalysisRequest = z.infer<
  typeof BroadcastVaultAnalysisRequestSchema
>;

export const BroadcastVaultAnalysisDispatchResultSchema = z.object({
  contentId: z.number(),
  contentName: z.string().optional(),
  status: z.string(),
  message: z.string().nullable().optional(),
});
export type BroadcastVaultAnalysisDispatchResult = z.infer<
  typeof BroadcastVaultAnalysisDispatchResultSchema
>;

export const BroadcastVaultAnalysisDispatchResponseSchema = z.object({
  dispatchedCount: z.number().default(0),
  skippedCount: z.number().default(0),
  results: z.array(BroadcastVaultAnalysisDispatchResultSchema).default([]),
});
export type BroadcastVaultAnalysisDispatchResponse = z.infer<
  typeof BroadcastVaultAnalysisDispatchResponseSchema
>;

export const BroadcastVaultMatchTeamSchema = z.object({
  name: NullableStringSchema,
  abbreviation: NullableStringSchema,
  pictureUrl: NullableStringSchema,
});
export type BroadcastVaultMatchTeam = z.infer<
  typeof BroadcastVaultMatchTeamSchema
>;

export const BroadcastVaultMatchSchema = z.object({
  matchId: z.string(),
  competitionKey: NullableStringSchema,
  competitionName: NullableStringSchema,
  stageName: NullableStringSchema,
  matchDate: NullableStringSchema,
  home: BroadcastVaultMatchTeamSchema,
  away: BroadcastVaultMatchTeamSchema,
  homeScore: z.number().nullable().optional(),
  awayScore: z.number().nullable().optional(),
  contentIds: z.array(z.number()).default([]),
});
export type BroadcastVaultMatch = z.infer<typeof BroadcastVaultMatchSchema>;

export const BroadcastVaultContentVariantSchema = z.object({
  type: z.string(),
  height: z.number(),
  url: z.string(),
});
export type BroadcastVaultContentVariant = z.infer<
  typeof BroadcastVaultContentVariantSchema
>;

export const BroadcastVaultProjectContentSchema = z.object({
  contentId: z.number(),
  name: z.string(),
  publicUrl: NullableStringSchema,
  thumbnailPublicUrl: NullableStringSchema,
  duration: NullableNumberSchema,
  width: NullableNumberSchema,
  height: NullableNumberSchema,
  variants: z.array(BroadcastVaultContentVariantSchema).default([]),
});
export type BroadcastVaultProjectContent = z.infer<
  typeof BroadcastVaultProjectContentSchema
>;
